import React, { useState, useEffect, useCallback } from 'react';
import {
  Show,
  TextField,
  NumberField,
  TabbedShowLayout,
  Tab,
  FunctionField,
  useRecordContext,
  useNotify,
  useRefresh,
} from 'react-admin';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
  Button,
  Rating,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Alert
} from '@mui/material';
import {
  Inventory,
  Star,
  LocalOffer,
  Visibility,
  Edit,
  Delete,
  CheckCircle,
  Cancel,
  Pending,
  ThumbUp
} from '@mui/icons-material';
import { buildApiUrl, resolveImageUrl } from '../config/api';

const ProductShowHeading = () => {
  const record = useRecordContext();
  if (!record) return null;

  return (
    <Box sx={{ mb: 3 }}>
      <Card sx={{ p: 3, bgcolor: 'primary.dark' }}>
        <Typography variant="overline" color="white" sx={{ letterSpacing: 1 }}>
          Produs
        </Typography>
        <Typography variant="h4" color="white" sx={{ mt: 1, fontWeight: 'bold' }}>
          {record.name}
        </Typography>
        <Typography variant="body2" color="rgba(255,255,255,0.8)">
          SKU: {record.sku} • Categorie: {record.category}
        </Typography>
      </Card>
    </Box>
  );
};

// Cross-Sell Display Component
const CrossSellDisplay = ({ productId }) => {
  const [crossSellData, setCrossSellData] = useState(null);

  useEffect(() => {
    const fetchCrossSellData = async () => {
      try {
        const response = await fetch(buildApiUrl(`products/${productId}/cross-sell`));
        if (response.ok) {
          const data = await response.json();
          setCrossSellData(data);
        }
      } catch (error) {
        console.error('Failed to fetch cross-sell data:', error);
      }
    };

    if (productId) {
      fetchCrossSellData();
    }
  }, [productId]);

  if (!crossSellData) {
    return (
      <Box sx={{ textAlign: 'center', p: 2 }}>
        <Typography>Se încarcă datele...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {crossSellData?.crossSellProducts?.length > 0 ? (
        <Box>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
              Toate accesoriile compatibile ({crossSellData.totalCrossSells})
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {crossSellData.directCrossSells} asocieri directe + {crossSellData.reverseCrossSells} asocieri inverse
            </Typography>
          </Box>
          <Grid container spacing={2}>
            {crossSellData.crossSellProducts.map((product, index) => (
              <Grid size={{ xs: 12, md: 6 }} key={index}>
                <Card sx={{ border: '1px solid #e0e0e0' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      {product.images?.[0]?.url && (
                        <img
                          src={resolveImageUrl(product.images[0].url)}
                          alt={product.name}
                          style={{
                            width: 60,
                            height: 60,
                            objectFit: 'cover',
                            borderRadius: 4
                          }}
                        />
                      )}
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                          {product.name}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                          {product.brand} • {product.price} lei
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Chip label={product.category} size="small" variant="outlined" />
                          {product.stock > 0 ? (
                            <Chip label="În stoc" size="small" color="success" />
                          ) : (
                            <Chip label="Stoc epuizat" size="small" color="error" />
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      ) : (
        <Box sx={{ textAlign: 'center', p: 4, bgcolor: 'grey.50', borderRadius: 2 }}>
          <Typography variant="h6" color="textSecondary" gutterBottom>
            Fără produse asociate
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Acest produs nu are accesorii compatibile configurate.
          </Typography>
          <Button
            variant="contained"
            startIcon={<Edit />}
            onClick={() => window.open(`#/products/${productId}`, '_blank')}
          >
            Adaugă produse asociate
          </Button>
        </Box>
      )}
    </Box>
  );
};

// Review Overview Stats Component
const ReviewOverviewStats = ({ record }) => {
  const [reviewStats, setReviewStats] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await fetch(buildApiUrl(`reviews/stats/${record.id}`));
        if (response.ok) {
          const stats = await response.json();
          setReviewStats(stats);
        }
      } catch (error) {
        console.error('Failed to fetch review stats:', error);
      }
    };

    if (record?.id) {
      fetchStats();
    }
  }, [record?.id]);

  return (
    <Grid container spacing={3}>
      <Grid size={{ xs: 12, md: 4 }}>
        <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'warning.light', borderRadius: 2 }}>
          <Star sx={{ fontSize: 40, color: 'white', mb: 1 }} />
          <Typography variant="h4" color="white">
            {reviewStats?.averageRating?.toFixed(1) || '0.0'}
          </Typography>
          <Typography variant="body2" color="white">Evaluare medie</Typography>
        </Box>
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'success.light', borderRadius: 2 }}>
          <Typography variant="h4" color="white">
            {reviewStats?.totalReviews || 0}
          </Typography>
          <Typography variant="body2" color="white">Total recenzii</Typography>
        </Box>
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'info.light', borderRadius: 2 }}>
          <Typography variant="h4" color="white">
            {reviewStats?.ratingDistribution?.[5] || 0}
          </Typography>
          <Typography variant="body2" color="white">Recenzii cu 5 stele</Typography>
        </Box>
      </Grid>
    </Grid>
  );
};

// Reviews Management Component
const ProductReviewsTab = () => {
  const record = useRecordContext();
  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, reviewId: null });
  const notify = useNotify();
  const refresh = useRefresh();

  const fetchReviews = useCallback(async () => {
    try {
      const response = await fetch(buildApiUrl(`reviews/product/${record.id}?page=1&limit=50`));
      if (response.ok) {
        const data = await response.json();
        setReviews(data.reviews || []);
      }
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
    } finally {
      setLoading(false);
    }
  }, [record.id]);

  const fetchReviewStats = useCallback(async () => {
    try {
      const response = await fetch(buildApiUrl(`reviews/stats/${record.id}`));
      if (response.ok) {
        const stats = await response.json();
        setReviewStats(stats);
      }
    } catch (error) {
      console.error('Failed to fetch review stats:', error);
    }
  }, [record.id]);

  useEffect(() => {
    if (record?.id) {
      fetchReviews();
      fetchReviewStats();
    }
  }, [record?.id, fetchReviews, fetchReviewStats]);

  const handleStatusChange = async (reviewId, newStatus) => {
    try {
      const response = await fetch(buildApiUrl(`reviews/admin/${reviewId}/status`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        notify(`Recenzia a fost ${newStatus === 'approved' ? 'aprobată' : 'respinsă'} cu succes`, { type: 'success' });
        fetchReviews();
        fetchReviewStats();
        refresh();
      } else {
        throw new Error('Failed to update review status');
      }
    } catch (error) {
      notify('Eroare la actualizarea statusului recenziei', { type: 'error' });
    }
  };

  const handleDeleteReview = async () => {
    try {
      const response = await fetch(buildApiUrl(`reviews/${deleteDialog.reviewId}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        notify('Recenzia a fost ștearsă cu succes', { type: 'success' });
        fetchReviews();
        fetchReviewStats();
        refresh();
      } else {
        throw new Error('Failed to delete review');
      }
    } catch (error) {
      notify('Eroare la ștergerea recenziei', { type: 'error' });
    } finally {
      setDeleteDialog({ open: false, reviewId: null });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'success';
      case 'rejected': return 'error';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <Typography>Se încarcă recenziile...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Review Stats Summary */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom color="primary">
            Statistici recenzii
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 3 }}>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'primary.light', borderRadius: 2 }}>
                <Star sx={{ fontSize: 40, color: 'white', mb: 1 }} />
                <Typography variant="h4" color="white">
                  {reviewStats?.averageRating?.toFixed(1) || '0.0'}
                </Typography>
                <Typography variant="body2" color="white">Evaluare medie</Typography>
              </Box>
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'success.light', borderRadius: 2 }}>
                <Typography variant="h4" color="white">
                  {reviewStats?.totalReviews || 0}
                </Typography>
                <Typography variant="body2" color="white">Total recenzii</Typography>
              </Box>
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'warning.light', borderRadius: 2 }}>
                <Pending sx={{ fontSize: 40, color: 'white', mb: 1 }} />
                <Typography variant="h4" color="white">
                  {reviews.filter(r => r.status === 'pending').length}
                </Typography>
                <Typography variant="body2" color="white">În așteptare</Typography>
              </Box>
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'info.light', borderRadius: 2 }}>
                <CheckCircle sx={{ fontSize: 40, color: 'white', mb: 1 }} />
                <Typography variant="h4" color="white">
                  {reviews.filter(r => r.status === 'approved').length}
                </Typography>
                <Typography variant="body2" color="white">Aprobate</Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Reviews List */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" color="primary">
              Recenzii produs ({reviews.length})
            </Typography>
            <Button
              variant="outlined"
              startIcon={<Star />}
              onClick={() => window.open(`#/reviews?filter=%7B"productId":"${record.id}"%7D`, '_blank')}
            >
              Gestionează recenziile
            </Button>
          </Box>
          <Divider sx={{ mb: 3 }} />

          {reviews.length === 0 ? (
            <Box sx={{ textAlign: 'center', p: 4, bgcolor: 'grey.50', borderRadius: 2 }}>
              <Star sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="textSecondary" gutterBottom>
                Nu există recenzii
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Acest produs nu a primit încă recenzii.
              </Typography>
            </Box>
          ) : (
            <List>
              {reviews.slice(0, 10).map((review) => (
                <ListItem key={review._id} sx={{ border: 1, borderColor: 'grey.200', borderRadius: 1, mb: 2 }}>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                            {review.title}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                            <Rating value={review.rating} size="small" readOnly />
                            <Typography variant="body2" color="textSecondary">
                              de {review.userName}
                            </Typography>
                            <Chip
                              label={review.status}
                              color={getStatusColor(review.status)}
                              size="small"
                            />
                          </Box>
                        </Box>
                      </Box>
                    }
                    secondary={
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          {review.comment.substring(0, 200)}
                          {review.comment.length > 200 && '...'}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <ThumbUp sx={{ fontSize: 14 }} />
                          <Typography variant="caption">{review.helpfulVotes} utile</Typography>
                          <Typography variant="caption" color="textSecondary">
                            • {new Date(review.createdAt).toLocaleDateString()}
                          </Typography>
                        </Box>
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {review.status === 'pending' && (
                        <>
                          <IconButton
                            color="success"
                            onClick={() => handleStatusChange(review._id, 'approved')}
                            title="Aprobă recenzia"
                          >
                            <CheckCircle />
                          </IconButton>
                          <IconButton
                            color="error"
                            onClick={() => handleStatusChange(review._id, 'rejected')}
                            title="Respinge recenzia"
                          >
                            <Cancel />
                          </IconButton>
                        </>
                      )}
                      {review.status === 'rejected' && (
                        <IconButton
                          color="success"
                          onClick={() => handleStatusChange(review._id, 'approved')}
                          title="Aprobă recenzia"
                        >
                          <CheckCircle />
                        </IconButton>
                      )}
                      {review.status === 'approved' && (
                        <IconButton
                          color="warning"
                          onClick={() => handleStatusChange(review._id, 'pending')}
                          title="Setează în așteptare"
                        >
                          <Pending />
                        </IconButton>
                      )}
                      <IconButton
                        color="error"
                        onClick={() => setDeleteDialog({ open: true, reviewId: review._id })}
                        title="Șterge recenzia"
                      >
                        <Delete />
                      </IconButton>
                      <IconButton
                        color="primary"
                        onClick={() => window.open(`#/reviews/${review._id}/show`, '_blank')}
                        title="Vezi recenzia"
                      >
                        <Visibility />
                      </IconButton>
                    </Box>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}

          {reviews.length > 10 && (
            <Box sx={{ textAlign: 'center', mt: 3 }}>
              <Alert severity="info">
                Se afișează primele 10 recenzii. Apasă "Gestionează recenziile" pentru a vedea toate cele {reviews.length} recenzii.
              </Alert>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, reviewId: null })}
      >
        <DialogTitle>Șterge recenzia</DialogTitle>
        <DialogContent>
          <Typography>
            Sunteți sigur că doriți să ștergeți definitiv această recenzie? Această acțiune nu poate fi anulată.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, reviewId: null })}>
            Anulează
          </Button>
          <Button onClick={handleDeleteReview} color="error" variant="contained">
            Șterge
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const ProductShowContent = () => (
  <>
    <ProductShowHeading />
    <TabbedShowLayout>
      {/* Images Tab */}
      <Tab label="Imagini">
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom color="primary">
              Imagini produs
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <FunctionField
              render={record => (
                <Box>
                  {record.images?.length > 0 ? (
                    <Grid container spacing={3}>
                      {record.images.map((image, index) => (
                        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={index}>
                          <Card sx={{ position: 'relative', overflow: 'hidden' }}>
                            <Box sx={{ position: 'relative' }}>
                              <img
                                src={image.url}
                                alt={`Product image ${index + 1}`}
                                style={{
                                  width: '100%',
                                  height: 200,
                                  objectFit: 'cover'
                                }}
                              />
                              {image.isPrimary && (
                                <Chip
                                  label="Principală"
                                  color="primary"
                                  size="small"
                                  sx={{
                                    position: 'absolute',
                                    top: 8,
                                    right: 8,
                                    backgroundColor: 'rgba(0,0,0,0.7)',
                                    color: 'white'
                                  }}
                                />
                              )}
                            </Box>
                            <CardContent sx={{ p: 2 }}>
                              <Typography variant="body2" color="textSecondary">
                                Imagine {index + 1}
                              </Typography>
                              {image.alt && (
                                <Typography variant="caption" display="block">
                                  Alt: {image.alt}
                                </Typography>
                              )}
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Box sx={{ textAlign: 'center', p: 4, bgcolor: 'grey.50', borderRadius: 2 }}>
                      <Typography variant="h6" color="textSecondary" gutterBottom>
                        Fără imagini
                      </Typography>
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        Acest produs nu are imagini încărcate.
                      </Typography>
                      <FunctionField
                        render={record => (
                          <Button
                            variant="contained"
                            startIcon={<Edit />}
                            onClick={() => window.open(`#/products/${record.id}`, '_blank')}
                          >
                            Adaugă imagini
                          </Button>
                        )}
                      />
                    </Box>
                  )}
                </Box>
              )}
            />
          </CardContent>
        </Card>
      </Tab>

      {/* Overview Tab */}
      <Tab label="Prezentare">
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom color="primary">
              Prezentare produs
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 3 }}>
                <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'primary.light', borderRadius: 2 }}>
                  <LocalOffer sx={{ fontSize: 40, color: 'white', mb: 1 }} />
                  <Typography variant="h5" color="white">
                    <NumberField source="price" />
                  </Typography>
                  <Typography variant="body2" color="white">Preț curent</Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'success.light', borderRadius: 2 }}>
                  <Inventory sx={{ fontSize: 40, color: 'white', mb: 1 }} />
                  <Typography variant="h5" color="white">
                    <NumberField source="stock" />
                  </Typography>
                  <Typography variant="body2" color="white">În stoc</Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'info.light', borderRadius: 2 }}>
                  <Visibility sx={{ fontSize: 40, color: 'white', mb: 1 }} />
                  <Typography variant="h6" color="white">
                    <TextField source="status" />
                  </Typography>
                  <Typography variant="body2" color="white">Status</Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'warning.light', borderRadius: 2 }}>
                  <Star sx={{ fontSize: 40, color: 'white', mb: 1 }} />
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <FunctionField
                      render={record => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center' }}>
                          {record.featured && <Chip label="Recomandat" size="small" color="primary" />}
                          {record.newProduct && <Chip label="Nou" size="small" color="success" />}
                          {record.onSale && <Chip label="La reducere" size="small" color="error" />}
                        </Box>
                      )}
                    />
                  </Box>
                  <Typography variant="body2" color="white">Etichete produs</Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom color="primary">
              Prezentare recenzii
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <FunctionField render={record => <ReviewOverviewStats record={record} />} />
          </CardContent>
        </Card>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">Acțiuni rapide</Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FunctionField
                  render={record => (
                    <Button
                      variant="contained"
                      startIcon={<Edit />}
                      onClick={() => window.open(`#/products/${record.id}`, '_blank')}
                    >
                      Editează produs
                    </Button>
                  )}
                />
                <FunctionField
                  render={record => (
                    <Button
                      variant="outlined"
                      startIcon={<Visibility />}
                      onClick={() => window.open(`/product/${record.slug}`, '_blank')}
                    >
                      Vezi în magazin
                    </Button>
                  )}
                />
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Tab>

      {/* Structured Description Tab */}
      <Tab label="Descriere structurată">
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">Descriere produs</Typography>
              <FunctionField
                render={record => (
                  <Button
                    variant="outlined"
                    startIcon={<Edit />}
                    size="small"
                    onClick={() => window.open(`#/products/${record.id}`, '_blank')}
                    sx={{ ml: 2 }}
                  >
                    Editează structura
                  </Button>
                )}
              />
            </Box>
            <Divider sx={{ mb: 3 }} />

            <FunctionField
              render={record => {
                const sections = record.structuredDescription?.sections;
                const hasSections = Array.isArray(sections) && sections.length > 0;
                return (
                  <Box>
                    {hasSections ? (
                      <Box sx={{ space: 3 }}>
                        {sections.map((section, sectionIndex) => (
                          <Card key={sectionIndex} sx={{ mb: 3, border: '1px solid #e0e0e0' }}>
                            <CardContent>
                              <Typography
                                variant="h6"
                                sx={{ color: 'primary.main', mb: 2, display: 'flex', alignItems: 'center' }}
                              >
                                <span style={{ fontSize: '1.5rem', marginRight: '12px' }}>
                                  {section.icon}
                                </span>
                                {section.title}
                              </Typography>
                              <Box sx={{ space: 2 }}>
                                {section.points?.map((point, pointIndex) => (
                                  <Box
                                    key={pointIndex}
                                    sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}
                                  >
                                    <Box
                                      sx={{
                                        width: 8,
                                        height: 8,
                                        bgcolor: 'primary.main',
                                        borderRadius: '50%',
                                        mt: 1,
                                        mr: 2,
                                        flexShrink: 0
                                      }}
                                    />
                                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                                      {point}
                                    </Typography>
                                  </Box>
                                ))}
                              </Box>
                            </CardContent>
                          </Card>
                        ))}
                      </Box>
                    ) : (
                      <Box sx={{ textAlign: 'center', p: 4, bgcolor: 'grey.50', borderRadius: 2 }}>
                        <Typography variant="h6" color="textSecondary" gutterBottom>
                          Fără descriere structurată
                        </Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                          Adaugă o descriere structurată pentru a prezenta caracteristicile produsului.
                        </Typography>
                        <FunctionField
                          render={record => (
                            <Button
                              variant="contained"
                              startIcon={<Edit />}
                              onClick={() => window.open(`#/products/${record.id}`, '_blank')}
                            >
                              Adaugă descriere structurată
                            </Button>
                          )}
                        />
                      </Box>
                    )}
                  </Box>
                );
              }}
            />
          </CardContent>
        </Card>
      </Tab>

      {/* Romanian Specifications Tab */}
      <Tab label="Specificații">
        {/* Hardware & Display Romanian */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary">
                  Specificații Hardware
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ space: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="textSecondary">Model Procesor:</Typography>
                    <TextField source="romanianSpecs.hardware.modelProcesor" />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="textSecondary">Frecvență:</Typography>
                    <TextField source="romanianSpecs.hardware.frecventa" />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="textSecondary">Memorie RAM:</Typography>
                    <TextField source="romanianSpecs.hardware.memorieRAM" />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="textSecondary">Capacitate Stocare:</Typography>
                    <TextField source="romanianSpecs.hardware.capacitateStocare" />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary">
                  Specificații Display
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ space: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="textSecondary">Diagonala Display:</Typography>
                    <TextField source="romanianSpecs.display.diagonalaDisplay" />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="textSecondary">Tehnologie Display:</Typography>
                    <TextField source="romanianSpecs.display.tehnologieDisplay" />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="textSecondary">Rezoluție Display:</Typography>
                    <TextField source="romanianSpecs.display.rezolutieDisplay" />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Package & Compatibility */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom color="primary">
              Pachet și Compatibilitate
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                  Conținut Pachet:
                </Typography>
                <TextField source="romanianSpecs.package.continutPachet" />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Destinat pentru:
                </Typography>
                <TextField source="romanianSpecs.compatibility.destinatPentru" />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Tip Montare:
                </Typography>
                <TextField source="romanianSpecs.compatibility.tipMontare" />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* General Info */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom color="primary">
              Informații Generale
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 3 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                  <Typography variant="body2" color="textSecondary">SKU</Typography>
                  <Typography variant="h6">
                    <TextField source="romanianSpecs.general.sku" />
                  </Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                  <Typography variant="body2" color="textSecondary">Brand</Typography>
                  <Typography variant="h6">
                    <TextField source="romanianSpecs.general.brand" />
                  </Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                  <Typography variant="body2" color="textSecondary">Categorii</Typography>
                  <Typography variant="h6">
                    <TextField source="romanianSpecs.general.categorii" />
                  </Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                  <Typography variant="body2" color="textSecondary">Sistem Operare</Typography>
                  <Typography variant="h6">
                    <TextField source="romanianSpecs.general.sistemOperare" />
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Tab>

      {/* Reviews Tab */}
      <Tab label="Recenzii și Evaluări">
        <ProductReviewsTab />
      </Tab>

      {/* SEO & Pricing Tab */}
      <Tab label="SEO și Prețuri">
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom color="primary">
              Informații prețuri
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 4 }}>
                <Box sx={{ textAlign: 'center', p: 3, bgcolor: 'success.light', borderRadius: 2 }}>
                  <Typography variant="h4" color="white">
                    <NumberField source="price" />
                  </Typography>
                  <Typography variant="body1" color="white">Preț curent</Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Box sx={{ textAlign: 'center', p: 3, bgcolor: 'info.light', borderRadius: 2 }}>
                  <Typography variant="h4" color="white">
                    <NumberField source="originalPrice" />
                  </Typography>
                  <Typography variant="body1" color="white">Preț original</Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Box sx={{ textAlign: 'center', p: 3, bgcolor: 'warning.light', borderRadius: 2 }}>
                  <Typography variant="h4" color="white">
                    <NumberField source="discount" />%
                  </Typography>
                  <Typography variant="body1" color="white">Reducere</Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom color="primary">
              Informații SEO
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <Box sx={{ space: 3 }}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Titlu SEO:
                </Typography>
                <TextField source="seoTitle" />
              </Box>
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Descriere SEO:
                </Typography>
                <TextField source="seoDescription" />
              </Box>
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                  Etichete:
                </Typography>
                <FunctionField
                  render={record => (
                    <Box>
                      {record.tags?.length > 0 ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {record.tags.map((tag, index) => (
                            <Chip key={index} label={tag} variant="outlined" color="secondary" />
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          Nu au fost specificate etichete
                        </Typography>
                      )}
                    </Box>
                  )}
                />
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Tab>

      {/* Cross-Sell Tab */}
      <Tab label="Produse asociate">
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom color="primary">
              Produse asociate (Accesorii compatibile)
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <FunctionField render={record => <CrossSellDisplay productId={record.id} />} />
          </CardContent>
        </Card>
      </Tab>
    </TabbedShowLayout>
  </>
);


export const ProductShow = () => (
  <Show
    sx={{
      '& .RaShow-card': {
        borderRadius: '16px',
        border: '1px solid #e4e7ec',
        boxShadow: 'none'
      }
    }}
  >
    <ProductShowContent />
  </Show>
);

export default ProductShow;
